import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { selectSampleErrorsPayload, selectIsStudySaved } from '../../state/selectors';
import { MissingSamples } from '../../../shared/models/result.model';
import { ReportContentItem } from '../../../shared/models/run.model';
import { AppUtilityService } from '../../../shared/services/app-utility.service';
import { ColumnSelection, CommonDialogData } from '../../../shared/models/dialog.model';
import { chunkColumnsForPrint, describePrintPage, PrintPageFit } from '../../../shared/utils/print-column.util';

type SampleErrorsColumn = {
  reportContentItemId: string;
  kitLot: string;
  runId: string;
  assay: string;
  beadRegion: number | string;
  isRefKitLot: boolean;
};

type SampleErrorsRow = {
  sampleId: string;
  values: SampleErrorsCell[];
};

type SampleErrorsCell = {
  text: string;
  isAssayFailure: boolean;
  isErrorMessage: boolean;
  fullMessage?: string;
};

@Component({
  selector: 'app-sample-errors',
  templateUrl: './sample-errors.component.html',
  styleUrl: './sample-errors.component.scss'
})
export class SampleErrorsComponent implements OnInit {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  private readonly internalQcBeadRegions = new Set<number>([34, 98]);
  /**
   * These columns carry sample error messages, not numbers, so they are far wider than any other
   * report's — which is why this screen fits two to a page where the numeric grids fit six.
   */
  private readonly printPageFit: PrintPageFit = { labelWidthMm: 34, columnWidthMm: 72 };
  private readonly selectedColumnsSubject = new BehaviorSubject<ColumnSelection[] | null>(null);
  isPrintView = false;
  isPrintMode = false;

  vm$ = combineLatest([
    this.store.select(selectSampleErrorsPayload),
    this.selectedColumnsSubject
  ]).pipe(
    map(([payload, selectedColumns]) => {
      const {
        panelName,
        runType,
        testSpec,
        refKitLot,
        appState,
        calculationResult,
        calculationAdjustedResult,
        reportContentResponse
      } = payload;

      const selectedCalculationResult = calculationAdjustedResult ?? calculationResult;
      const hasCalculation = !!selectedCalculationResult;
      const reportContentItems = reportContentResponse?.reportContentItems ?? [];
      const { columns: allColumns, rows: allRows } = this.buildTable(
        selectedCalculationResult?.runStatus ?? [],
        reportContentItems,
        refKitLot ?? ''
      );

      let baseColumns = allColumns;
      let baseRows = allRows;

      // Print-only behavior: hide columns that never have missing-sample data.
      // If every cell in a column is "-", it should not appear in print preview/browser print.
      if (this.isPrintView && allColumns.length) {
        const printableColumnIndexes = allColumns
          .map((_, index) => index)
          .filter(index =>
            allRows.some(row => {
              const cell = row.values[index];
              return !!cell && cell.text !== '-';
            })
          );

        baseColumns = printableColumnIndexes.map(index => allColumns[index]);
        baseRows = allRows.map(row => ({
          sampleId: row.sampleId,
          values: printableColumnIndexes.map(index => row.values[index])
        }));
      }

      const tableColumns = baseColumns.map(column => ({
        columnName: `${column.kitLot} ${column.runId} ${column.beadRegion}`.trim(),
        columnKey: column.reportContentItemId,
        kitLot: column.kitLot,
        instrumentId: column.runId,
        beadRegion: Number(column.beadRegion),
        isSelected: true
      } as ColumnSelection));

      const normalizedSelection = this.normalizeSelectedColumns(selectedColumns, tableColumns);
      const selectedKeys = new Set(
        normalizedSelection
          .filter(column => column.isSelected)
          .map(column => column.columnKey ?? column.columnName)
      );
      const selectedIndices = baseColumns
        .map((column, index) => ({ column, index }))
        .filter(entry => selectedKeys.has(entry.column.reportContentItemId))
        .map(entry => entry.index);

      const columns = selectedIndices.map(index => baseColumns[index]);
      const rows = baseRows.map(row => ({
        sampleId: row.sampleId,
        values: selectedIndices.map(index => row.values[index])
      }));
      const printPages = this.getPrintPages(columns);
      const hasVisibleSampleErrors = rows.length > 0 && columns.length > 0;

      return {
        panelName: panelName || '-',
        runType: runType || '-',
        testSpec: testSpec || '-',
        assignmentSpec: appState?.selectedStudy?.assignSpec || 'Final',
        operator: appState?.loggedInUser || '-',
        studyName: appState?.selectedStudy?.studyName || '-',
        specVersion: appState?.selectedStudy?.panelSpecs?.revisionNumber || '-',
        appVersion: appState?.appVersion || '-',
        printContext: [
          runType,
          testSpec ? `Test Spec ${testSpec}` : '',
          appState?.selectedStudy?.panelSpecs?.revisionNumber
            ? `Spec Rev ${appState.selectedStudy.panelSpecs.revisionNumber}` : '',
          appState?.loggedInUser
        ].filter(Boolean).join(' · '),
        hasCalculation,
        hasSampleErrors: hasVisibleSampleErrors,
        columns,
        printPages,
        rows,
        tableColumns: normalizedSelection
      };
    })
  );

  constructor(
    private readonly store: Store,
    private readonly appUtilityService: AppUtilityService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isPrintView = this.router?.url?.includes('print');
    // The print route is print mode; there is no other reason to be on it. This used to be
    // switched on by the print menu opening over the page, which left the layout in screen mode
    // if the operator dismissed the menu.
    this.isPrintMode = this.isPrintView;
  }

  navigateToPrintView(): void {
    this.router.navigateByUrl('/(print:print/preview-sample-errors)');
  }

  handleSelectColumns(selectedColumns: ColumnSelection[]): void {
    this.selectedColumnsSubject.next(selectedColumns ?? []);
  }

  handleMenuOpenStatus(isOpen: boolean): void {
    this.isPrintMode = isOpen;
  }

  getPrintPages(columns: SampleErrorsColumn[]): SampleErrorsColumn[][] {
    return chunkColumnsForPrint(columns, this.printPageFit);
  }

  /**
   * "Columns 3-4 of 9 · page 2 of 5" over a chunk page; empty when the report was not split.
   *
   * Takes the pages rather than reading them off the component: this screen's are computed in the
   * view model stream, not held in a field.
   */
  printPageCaption(pageIndex: number, pages: SampleErrorsColumn[][]): string {
    return describePrintPage(pageIndex, pages);
  }

  getPageRows(rows: SampleErrorsRow[], allColumns: SampleErrorsColumn[], pageColumns: SampleErrorsColumn[]): SampleErrorsRow[] {
    const pageIndices = pageColumns
      .map(column => allColumns.findIndex(entry => entry.reportContentItemId === column.reportContentItemId))
      .filter(index => index >= 0);
    return rows.map(row => ({
      sampleId: row.sampleId,
      values: pageIndices.map(index => row.values[index])
    }));
  }

  getPrintErrorLines(message?: string): string[] {
    const trimmed = (message ?? '').trim();
    if (!trimmed || trimmed === '-') {
      return ['-'];
    }
    const segments = trimmed.match(/[^;]+;?/g)?.map(segment => segment.trim()).filter(Boolean) ?? [];
    return segments.length ? segments : [trimmed];
  }

  private buildTable(
    runStatuses: Array<{ reportContentItemId: string; missingSamples?: MissingSamples[] }>,
    reportContentItems: ReportContentItem[],
    refKitLot: string
  ): { columns: SampleErrorsColumn[]; rows: SampleErrorsRow[] } {
    const sortedReportContentItems = [...(reportContentItems ?? [])].sort((a, b) =>
      this.compareReportContentItems(a, b, refKitLot)
    );

    const reportContentById = new Map<string, ReportContentItem>(
      sortedReportContentItems.map(item => [item.reportContentItemId, item])
    );

    const trimmedRefKitLot = (refKitLot ?? '').trim();
    const columns: SampleErrorsColumn[] = sortedReportContentItems.map(item => ({
      reportContentItemId: item.reportContentItemId,
      kitLot: item.lot ?? '-',
      runId: item.runId ?? '-',
      assay: item.assay ?? '-',
      beadRegion: item.region ?? '-',
      isRefKitLot: trimmedRefKitLot !== '' && item.lot === trimmedRefKitLot
    }));

    const sortedColumns = [...columns].sort((a, b) => this.compareSampleErrorColumns(a, b, refKitLot));

    const allSampleIds = new Set<string>();
    const missingByReportContent = new Map<string, Map<string, MissingSamples>>();

    runStatuses.forEach(runStatus => {
      const missing = runStatus.missingSamples ?? [];
      const sampleMap = new Map<string, MissingSamples>();
      missing.forEach(sample => {
        const sampleId = sample.accessionNo?.trim();
        if (!sampleId) {
          return;
        }
        allSampleIds.add(sampleId);
        sampleMap.set(sampleId, sample);
      });
      missingByReportContent.set(runStatus.reportContentItemId, sampleMap);
    });

    const orderedSampleIds = Array.from(allSampleIds).sort((a, b) => a.localeCompare(b));
    const rows: SampleErrorsRow[] = orderedSampleIds.map(sampleId => ({
      sampleId,
      values: sortedColumns.map(column => {
        const sample = missingByReportContent.get(column.reportContentItemId)?.get(sampleId);
        if (!sample) {
          return { text: '-', isAssayFailure: false, isErrorMessage: false };
        }
        return this.formatSampleErrorCell(sample);
      })
    }));

    // Keep only columns that can be mapped to report content metadata.
    const validColumns = sortedColumns.filter(col => reportContentById.has(col.reportContentItemId));
    const validColumnIds = new Set(validColumns.map(col => col.reportContentItemId));
    const filteredRows: SampleErrorsRow[] = rows.map(row => ({
      sampleId: row.sampleId,
      values: sortedColumns
        .map((col, idx) => ({ colId: col.reportContentItemId, value: row.values[idx] }))
        .filter(entry => validColumnIds.has(entry.colId))
        .map(entry => entry.value)
    }));

    return { columns: validColumns, rows: filteredRows };
  }

  private normalizeSelectedColumns(
    selectedColumns: ColumnSelection[] | null,
    tableColumns: ColumnSelection[]
  ): ColumnSelection[] {
    if (!selectedColumns?.length) {
      return tableColumns;
    }
    const validKeys = new Set(tableColumns.map(column => column.columnKey ?? column.columnName));
    const selectedByKey = new Map(
      selectedColumns
        .filter(column => validKeys.has(column.columnKey ?? column.columnName))
        .map(column => [column.columnKey ?? column.columnName, column])
    );
    if (!selectedByKey.size) {
      return tableColumns;
    }
    return tableColumns.map(column => {
      const key = column.columnKey ?? column.columnName;
      const selected = selectedByKey.get(key);
      return selected ? { ...column, isSelected: selected.isSelected } : { ...column, isSelected: false };
    });
  }

  private compareReportContentItems(a: ReportContentItem, b: ReportContentItem, refKitLot: string): number {
    const regionCompare = this.compareBeadRegions(a.region, b.region);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    const refLot = (refKitLot ?? '').trim();
    const aLot = (a.lot ?? '');
    const bLot = (b.lot ?? '');
    if (refLot) {
      const aIsRef = aLot === refLot;
      const bIsRef = bLot === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }

    const kitCompare = aLot.localeCompare(bLot);
    if (kitCompare !== 0) {
      return kitCompare;
    }

    const runCompare = (a.runId ?? '').localeCompare(b.runId ?? '');
    if (runCompare !== 0) {
      return runCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
    const leftRegion = this.parseRegionValue(left);
    const rightRegion = this.parseRegionValue(right);
    const leftInternal = this.internalQcBeadRegions.has(leftRegion);
    const rightInternal = this.internalQcBeadRegions.has(rightRegion);
    if (leftInternal !== rightInternal) {
      return leftInternal ? 1 : -1;
    }
    return leftRegion - rightRegion;
  }

  private parseRegionValue(region: string | number | undefined): number {
    if (typeof region === 'number') {
      return Number.isNaN(region) ? 0 : region;
    }
    const parsed = Number(region);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private compareSampleErrorColumns(a: SampleErrorsColumn, b: SampleErrorsColumn, refKitLot: string): number {
    const regionCompare = this.compareBeadRegions(a.beadRegion, b.beadRegion);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    const refLot = (refKitLot ?? '').trim();
    if (refLot) {
      const aIsRef = a.kitLot === refLot;
      const bIsRef = b.kitLot === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }

    const kitCompare = a.kitLot.localeCompare(b.kitLot);
    if (kitCompare !== 0) {
      return kitCompare;
    }

    const runCompare = a.runId.localeCompare(b.runId);
    if (runCompare !== 0) {
      return runCompare;
    }

    return a.assay.localeCompare(b.assay);
  }

  private formatSampleErrorCell(sample: MissingSamples): SampleErrorsCell {
    if (!sample.discarded) {
      return {
        text: `# Run: ${sample.totalNumRepOk ?? 0}`,
        isAssayFailure: false,
        isErrorMessage: false
      };
    }

    const message = (sample.errorMessage ?? '').trim() || '-';
    return {
      text: message,
      isAssayFailure: !!sample.assayFailure,
      isErrorMessage: message !== '-',
      fullMessage: message
    };
  }

  openErrorDialog(message?: string): void {
    const trimmed = (message ?? '').trim();
    if (!trimmed || trimmed === '-') {
      return;
    }
    const dialogData = new CommonDialogData();
    dialogData.title = 'Sample Error';
    dialogData.message = trimmed;
    dialogData.okText = 'Close';
    dialogData.okAction = () => {};
    this.appUtilityService.displayAlertDialog(dialogData);
  }
}
